import React, { useState, useEffect, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Ban, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

/**
 * Diálogo para marcar una factura como ANULADA en el portal SRI.
 * ITER188: incluye paso 2 con reingreso selectivo de stock + reversa CxC.
 */
export default function MarcarAnuladaSriDialog({ factura, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [motivo, setMotivo] = useState("");
  const [fechaPortal, setFechaPortal] = useState("");
  const [preview, setPreview] = useState(null);
  const [reingresoModo, setReingresoModo] = useState("automatico");  // "automatico" | "manual" | "ninguno"
  const [seleccionadas, setSeleccionadas] = useState({});  // {idx: {checked, cantidad}}
  const [reversarCxc, setReversarCxc] = useState(true);
  const [pagosSaldoFavor, setPagosSaldoFavor] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (factura) {
      setStep(1);
      setMotivo("");
      setFechaPortal(new Date().toISOString().slice(0, 10));
      setPreview(null);
      setReingresoModo("automatico");
      setSeleccionadas({});
      setReversarCxc(true);
      setPagosSaldoFavor(true);
    }
  }, [factura]);

  const cargarPreview = async () => {
    if (!factura) return;
    setLoadingPreview(true);
    try {
      const { data } = await api.get(`/invoicing/facturas/${factura.id}/preview-reingreso`);
      setPreview(data);
      // Pre-select todas las líneas con cantidad pendiente > 0 (para modo manual)
      const sel = {};
      (data.lineas || []).forEach((l, i) => {
        sel[i] = { checked: l.cantidad_pendiente_reingreso > 0, cantidad: l.cantidad_pendiente_reingreso };
      });
      setSeleccionadas(sel);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingPreview(false);
    }
  };

  const nextStep = async () => {
    if (motivo.trim().length < 5) {
      toast.error("Debes indicar un motivo (mínimo 5 caracteres)");
      return;
    }
    await cargarPreview();
    setStep(2);
  };

  const totalPendiente = useMemo(() => (
    (preview?.lineas || []).reduce((s, l) => s + (+l.cantidad_pendiente_reingreso || 0), 0)
  ), [preview]);

  const submit = async () => {
    if (!factura) return;
    setSaving(true);
    try {
      const payload = {
        motivo: motivo.trim(),
        fecha_anulacion_sri: fechaPortal || null,
        reversar_cxc: reversarCxc,
        pagos_como_saldo_favor: pagosSaldoFavor,
      };
      if (reingresoModo === "automatico") {
        payload.reingreso_modo = "automatico";
      } else if (reingresoModo === "manual") {
        const lineas = (preview?.lineas || [])
          .map((l, i) => {
            const s = seleccionadas[i];
            if (!s?.checked || !s.cantidad || s.cantidad <= 0) return null;
            return {
              producto_comercial_id: l.producto_comercial_id,
              lote_id: l.lote_id,
              cantidad: +s.cantidad,
              unidad_venta: l.unidad_venta,
              unidades_por_caja: l.unidades_por_caja,
            };
          })
          .filter(Boolean);
        payload.reingreso_modo = "manual";
        payload.reingreso_lineas = lineas;
      }
      const { data } = await api.post(`/invoicing/facturas/${factura.id}/marcar-anulada-sri`, payload);
      const acc = data?._acciones || {};
      let msg = `Factura ${factura.numero} anulada`;
      if ((acc.reingreso || []).length > 0) msg += ` · ${acc.reingreso.length} lote(s) reingresado(s)`;
      if (acc.saldo_favor?.monto) msg += ` · Saldo a favor: $${acc.saldo_favor.monto.toFixed(2)}`;
      toast.success(msg);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!factura) return null;

  return (
    <Dialog open={!!factura} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="marcar-anulada-sri-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#b53a25]">
            <Ban size={18} /> Marcar factura como ANULADA en SRI · Paso {step} de 2
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-[#f5c477] bg-[#fef7ec] p-3 text-[#7a4000] flex gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold mb-1">Úsalo solo si ya anulaste este comprobante en el portal del SRI.</div>
                <div className="text-xs">
                  Cambiará el estado local a <b>ANULADO</b>. En el paso 2 podrás elegir si <b>reingresar productos al stock</b> y qué hacer con la <b>Cuenta por Cobrar</b>.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-[#5c7368]">Factura</span><div className="font-mono font-semibold text-[#15241d]">{factura.numero}</div></div>
              <div><span className="text-[#5c7368]">Cliente</span><div className="font-semibold text-[#15241d] truncate">{factura.cliente_nombre || "—"}</div></div>
              <div><span className="text-[#5c7368]">Total</span><div className="font-semibold text-[#15241d]">${(factura.total || 0).toFixed(2)}</div></div>
              <div><span className="text-[#5c7368]">Estado actual</span><div className="uppercase font-semibold">{factura.estado_sri || "—"}</div></div>
            </div>

            <div>
              <Label>Fecha de anulación en SRI</Label>
              <Input type="date" value={fechaPortal} onChange={(e) => setFechaPortal(e.target.value)} data-testid="anular-sri-fecha" />
            </div>

            <div>
              <Label>Motivo <span className="text-[#b53a25]">*</span></Label>
              <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej: Anulada en SRI por error en productos. Se emitirá nueva factura con los productos correctos."
                rows={3} data-testid="anular-sri-motivo" />
              <div className="text-[10px] text-[#5c7368] mt-1">Mínimo 5 caracteres.</div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 text-sm">
            {loadingPreview && <div className="text-center py-4 text-[#8ba397]">Cargando líneas…</div>}
            {preview && (
              <>
                {/* Reingreso stock */}
                <div className="border border-[#d1dbd6] rounded-md p-3 space-y-3">
                  <div className="font-semibold text-[#205c45] uppercase text-xs tracking-wider">1) Reingreso de productos al stock</div>
                  <div className="flex gap-4 text-xs">
                    <label className="flex items-center gap-1"><input type="radio" checked={reingresoModo === "automatico"} onChange={() => setReingresoModo("automatico")} data-testid="reingreso-modo-automatico" /> Automático (todo lo pendiente)</label>
                    <label className="flex items-center gap-1"><input type="radio" checked={reingresoModo === "manual"} onChange={() => setReingresoModo("manual")} data-testid="reingreso-modo-manual" /> Elegir líneas</label>
                    <label className="flex items-center gap-1"><input type="radio" checked={reingresoModo === "ninguno"} onChange={() => setReingresoModo("ninguno")} data-testid="reingreso-modo-ninguno" /> No reingresar</label>
                  </div>

                  {reingresoModo !== "ninguno" && (
                    <div className="border rounded max-h-64 overflow-y-auto" data-testid="reingreso-lineas-table">
                      <table className="w-full text-xs">
                        <thead className="bg-[#f7f9f8] sticky top-0">
                          <tr>
                            {reingresoModo === "manual" && <th className="p-1.5" style={{ width: 30 }}></th>}
                            <th className="text-left p-1.5">Producto</th>
                            <th className="text-left p-1.5">Lote</th>
                            <th className="text-right p-1.5">Facturado</th>
                            <th className="text-right p-1.5">Ya devuelto (NC)</th>
                            <th className="text-right p-1.5">Pendiente</th>
                            {reingresoModo === "manual" && <th className="text-right p-1.5" style={{ width: 100 }}>Reingresar</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {(preview.lineas || []).map((l, i) => {
                            const s = seleccionadas[i] || {};
                            const bloqueada = l.cantidad_pendiente_reingreso <= 0 || !l.lote_id;
                            return (
                              <tr key={i} className={`border-t ${bloqueada ? "opacity-50" : ""}`} data-testid={`reingreso-row-${i}`}>
                                {reingresoModo === "manual" && (
                                  <td className="p-1.5">
                                    <Checkbox
                                      checked={!!s.checked}
                                      disabled={bloqueada}
                                      onCheckedChange={(v) => setSeleccionadas(prev => ({ ...prev, [i]: { ...prev[i], checked: !!v } }))}
                                      data-testid={`reingreso-check-${i}`}
                                    />
                                  </td>
                                )}
                                <td className="p-1.5">{l.producto_nombre || "—"}</td>
                                <td className="p-1.5 font-mono">{l.numero_lote || <span className="text-[#b53a25]">sin lote</span>}</td>
                                <td className="p-1.5 text-right tabular-nums">{l.cantidad_facturada} <span className="text-[9px] text-[#5c7368]">{l.unidad_venta?.[0]}</span></td>
                                <td className="p-1.5 text-right tabular-nums text-[#8a5500]">{l.cantidad_devuelta_ncs || 0}</td>
                                <td className="p-1.5 text-right tabular-nums font-semibold text-[#205c45]">{l.cantidad_pendiente_reingreso}</td>
                                {reingresoModo === "manual" && (
                                  <td className="p-1.5 text-right">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min={0}
                                      max={l.cantidad_pendiente_reingreso}
                                      value={s.cantidad ?? l.cantidad_pendiente_reingreso}
                                      onChange={(e) => setSeleccionadas(prev => ({ ...prev, [i]: { ...prev[i], cantidad: +e.target.value } }))}
                                      disabled={bloqueada || !s.checked}
                                      className="h-7 text-xs text-right"
                                      data-testid={`reingreso-cantidad-${i}`}
                                    />
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {reingresoModo === "automatico" && totalPendiente > 0 && (
                    <div className="text-[11px] text-[#5c7368]">Se reingresarán <b>{totalPendiente}</b> unidades en total a los lotes originales.</div>
                  )}
                  {reingresoModo === "automatico" && totalPendiente === 0 && (
                    <div className="text-[11px] text-[#8a5500]">No hay unidades pendientes de reingreso (todo ya fue devuelto por NCs previas).</div>
                  )}
                </div>

                {/* CxC */}
                <div className="border border-[#d1dbd6] rounded-md p-3 space-y-2">
                  <div className="font-semibold text-[#205c45] uppercase text-xs tracking-wider">2) Cuenta por Cobrar</div>
                  {preview.cxc ? (
                    <>
                      <div className="text-xs text-[#5c7368]">
                        CxC actual: Total <b className="text-[#15241d] tabular-nums">${(preview.cxc.monto_total || 0).toFixed(2)}</b> · Pagado <b className="text-[#15241d] tabular-nums">${(preview.cxc.monto_pagado || 0).toFixed(2)}</b> · Saldo <b className="text-[#15241d] tabular-nums">${(preview.cxc.saldo || 0).toFixed(2)}</b>
                      </div>
                      <label className="flex items-center gap-2 text-xs">
                        <Checkbox checked={reversarCxc} onCheckedChange={(v) => setReversarCxc(!!v)} data-testid="reversar-cxc-check" />
                        Anular esta CxC (evita cobros fantasmas)
                      </label>
                      {reversarCxc && preview.cxc_tiene_pagos_aplicados && (
                        <label className="flex items-center gap-2 text-xs ml-6" data-testid="saldo-favor-container">
                          <Checkbox checked={pagosSaldoFavor} onCheckedChange={(v) => setPagosSaldoFavor(!!v)} data-testid="saldo-favor-check" />
                          Convertir ${(preview.cxc.monto_pagado || 0).toFixed(2)} en <b>saldo a favor del cliente</b> (útil para aplicar a futura factura)
                        </label>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-[#5c7368]">Esta factura no tiene CxC asociada.</div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={onClose} disabled={saving} data-testid="anular-sri-cancel-btn">Cancelar</Button>
              <Button
                onClick={nextStep}
                disabled={motivo.trim().length < 5 || loadingPreview}
                className="bg-[#205c45] hover:bg-[#1a4a37]"
                data-testid="anular-sri-next-btn"
              >
                Siguiente <ArrowRight size={14} className="ml-1" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)} disabled={saving} data-testid="anular-sri-back-btn">
                <ArrowLeft size={14} className="mr-1" /> Atrás
              </Button>
              <Button
                onClick={submit}
                disabled={saving}
                className="bg-[#b53a25] hover:bg-[#8f2a18] text-white"
                data-testid="anular-sri-confirm-btn"
              >
                {saving ? "Guardando…" : "Anular y aplicar reingreso"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
